<template>
  <div class="container-fluid mt-4 bg-white px-0">
    <chart-header
      :title="$t('flexigo.dashboard.usage')"
      :has-data="!!usageItems.length"
      @filter-change="onFilterChange"
      @download="download"
    />
    <hr />
    <div class="px-3">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="chart-left-title">{{ $t('flexigo.dashboard.ridership') }}</div>
        <div class="chart-right-title">{{ $t('flexigo.dashboard.pooledRidesPercent') }}</div>
      </div>
      <highcharts ref="usageChart" :options="chartOptions"></highcharts>
    </div>
    <excel-downloader ref="excelDownloader" :data="excelData" :file-name="'Usage_Chart'" :sheet-name="'Usage'" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { format } from 'date-fns';
import { Chart } from 'highcharts-vue';
import {
  getXAxisFormatType,
  formatXAxisLabel,
  groupDataByFormatType,
  aggregateGroupedData,
  sortHourlyItemsByWindow,
} from '@/modules/Flexigo/helpers/chartXAxisFormatter';
import { exportChartAsPNG } from '@/modules/Flexigo/components/dashboard/otp/utils/chartExport';
import chartTooltipPositioner from '@/modules/Flexigo/helpers/chartTooltipPositioner';
import { lineSeriesWithoutPoint } from '@/modules/Flexigo/helpers/chartLineGaps';
import buildGapPlotLines from '@/modules/Flexigo/helpers/chartGapPlotLines';
import chartHeader from './ChartHeader.vue';
import ExcelDownloader from './ExcelDownloader.vue';

/**
 * Pooled rides as a percentage of completed rides for one bucket (a raw hourly row or an
 * aggregated day/week/month), or null when nothing was completed — including future days.
 * An undefined share is not 0%, so the line breaks there instead of dropping to 0
 * (VSER-14215).
 */
function pooledRidesPercentOf(item) {
  const completed = item?.completed || 0;
  if (completed === 0) return null;
  return parseFloat((((item?.pooledRides || 0) / completed) * 100).toFixed(2));
}

export default {
  name: 'UsageChart',
  components: {
    chartHeader,
    highcharts: Chart,
    ExcelDownloader,
  },
  data() {
    return {
      selectedDate: 'thisweek',
      filters: {
        startDate: null,
        endDate: null,
        fromHour: null,
        toHour: null,
      },
    };
  },
  computed: {
    ...mapGetters('onDemandDashboard', {
      usageItems: 'usageItems',
    }),
    xAxisFormatType() {
      return getXAxisFormatType(this.filters.startDate, this.filters.endDate, this.selectedDate);
    },
    // Items as the chart/excel/gap-markers all see them: raw for hourly, grouped+aggregated
    // otherwise. Shared so the three consumers don't re-run the grouping block inline.
    processedItems() {
      const items = this.usageItems || [];
      if (items.length === 0) return items;
      const formatType = this.xAxisFormatType;
      // Overnight operation windows wrap next-day early hours under the same operationDay;
      // reorder so 00:00 lands after 23:00 instead of before 20:00.
      if (formatType === 'hourly') {
        return sortHourlyItemsByWindow(items, formatType, this.filters.fromHour, this.filters.toHour);
      }
      const grouped = groupDataByFormatType(items, formatType);
      return Object.values(grouped).map((groupedItems) => {
        const aggregated = aggregateGroupedData(
          groupedItems,
          ['completed', 'cancelledByAdmin', 'cancelledByRider', 'riderNoShows', 'pooledRides'], // Sum
          [], // No average fields
        );
        // Keep the pooled-rides share on the aggregated bucket for excel/tooltip; null when
        // nothing was completed (future days, empty aggregates) so the line can break.
        aggregated.pooledRidesPercent = pooledRidesPercentOf(aggregated);
        return aggregated;
      });
    },
    // Dashed vertical markers with "+Nh"/"+Nd" labels where consecutive bars skip over
    // empty buckets (mirrors Shuttle RidershipByRouteChart).
    gapPlotLines() {
      return buildGapPlotLines(this.processedItems, this.xAxisFormatType);
    },
    excelData() {
      const items = this.processedItems;
      const formatType = this.xAxisFormatType;

      // Determine column header based on formatType
      const firstColumnHeader =
        formatType === 'hourly'
          ? this.$t('flexigo.dashboard.hour') || 'Hour'
          : this.$t('flexigo.dashboard.date') || 'Date';

      return items.map((item) => {
        const label = formatXAxisLabel(item, formatType, this.$i18n.locale);

        return {
          [firstColumnHeader]: label,
          [this.$t('flexigo.dashboard.completed')]: item?.completed || 0,
          [this.$t('flexigo.dashboard.cancelledByAdmin')]: item?.cancelledByAdmin || 0,
          [this.$t('flexigo.dashboard.cancelledByRider')]: item?.cancelledByRider || 0,
          [this.$t('flexigo.dashboard.riderNoShows')]: item?.riderNoShows || 0,
          [this.$t('flexigo.dashboard.pooledRidesPercent')]: item?.pooledRidesPercent || 0,
        };
      });
    },
    chartOptions() {
      const chartOptions = {
        chart: {
          backgroundColor: '#ffffff',
        },
        credits: {
          enabled: false,
        },
        exporting: {
          enabled: false,
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: [],
          crosshair: true,
          // Dashed vertical markers with "+Nh"/"+Nd" labels where consecutive bars skip
          // over empty buckets (mirrors Shuttle RidershipByRouteChart).
          plotLines: this.gapPlotLines,
        },
        yAxis: [
          {
            title: { text: '' },
            min: 0,
            tickInterval: 5,
            endOnTick: false,
            gridLineColor: '#f0f2f5',
          },
          {
            title: { text: '' },
            min: 0,
            max: 100,
            tickInterval: 10,
            endOnTick: false,
            opposite: true,
            gridLineWidth: 0,
          },
        ],
        plotOptions: {
          column: {
            borderWidth: 0,
            pointWidth: 14,
            pointPlacement: 'on',
            color: '#65728a',
          },
          spline: {
            lineWidth: 2,
            color: '#5b8ff9',
          },
        },
        tooltip: {
          shared: true,
          useHTML: true,
          backgroundColor: '#363647',
          style: { color: '#ffffff' },
          borderRadius: 6,
          positioner(labelWidth, labelHeight, point) {
            return chartTooltipPositioner(labelWidth, labelHeight, point, this.chart);
          },
          formatter() {
            let tooltipHtml = `<div class="dashboard-graph-tooltip"><div class="title">${this.x}</div>`;

            this.points.forEach((point) => {
              const seriesName = point.series.name;
              const value = point.y || 0;

              tooltipHtml += `
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="d-flex align-items-center">
                    <span class="text-label">${seriesName}</span>
                  </div>
                  <span class="text-value">${value}${point.series.type === 'spline' ? '%' : ''}</span>
                </div>
              `;
            });

            // A null line point is left out of the shared tooltip; list it as N/A so the metric
            // doesn't silently disappear on buckets where it has no value.
            lineSeriesWithoutPoint(this.points).forEach((series) => {
              tooltipHtml += `
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="d-flex align-items-center">
                    <span class="text-label">${series.name}</span>
                  </div>
                  <span class="text-value">N/A</span>
                </div>
              `;
            });

            tooltipHtml += '</div>';
            return tooltipHtml;
          },
        },
        legend: {
          squareSymbol: true,
          symbolRadius: 0,
          useHTML: true,
          margin: 30,
          zIndex: 1,
          labelFormatter() {
            return `<div style="color: #687692; font-weight: normal;">${this.name}</div>`;
          },
        },
        series: [
          {
            name: this.$t('flexigo.dashboard.completed'),
            type: 'column',
            yAxis: 0,
            color: '#5287e3',
            data: [],
          },
          {
            name: this.$t('flexigo.dashboard.cancelledByAdmin'),
            type: 'column',
            yAxis: 0,
            color: '#ff8b3d',

            data: [],
          },
          {
            name: this.$t('flexigo.dashboard.cancelledByRider'),
            type: 'column',
            yAxis: 0,
            color: '#d0342c',
            data: [],
          },
          {
            name: this.$t('flexigo.dashboard.riderNoShows'),
            type: 'column',
            yAxis: 0,
            color: '#687692',
            data: [],
          },
          {
            name: this.$t('flexigo.dashboard.pooledRidesPercent'),
            type: 'spline',
            yAxis: 1,
            data: [],
            color: '#26de81',
            marker: {
              fillColor: '#ffffff',
              lineWidth: 2,
              lineColor: '#26de81',
              radius: 5,
            },
          },
        ],
      };

      const items = this.processedItems;
      const formatType = this.xAxisFormatType;

      // Format X-axis labels
      chartOptions.xAxis.categories = items.map((item) => formatXAxisLabel(item, formatType, this.$i18n.locale));

      chartOptions.series[0].data = items.map((item) => item?.completed || 0);
      chartOptions.series[1].data = items.map((item) => item?.cancelledByAdmin || 0);
      chartOptions.series[2].data = items.map((item) => item?.cancelledByRider || 0);
      chartOptions.series[3].data = items.map((item) => item?.riderNoShows || 0);

      // Raw hourly rows carry pooledRides/completed counts; aggregated buckets were summed
      // above. Either way a bucket with no completed rides (which covers future days) gets
      // null so the line breaks instead of drawing 0%.
      chartOptions.series[4].data = items.map((item) => pooledRidesPercentOf(item));

      return chartOptions;
    },
  },
  methods: {
    ...mapActions('onDemandDashboard', ['fetchUsageData']),

    download(type) {
      if (type === 'image') {
        this.downloadChartAsImage();
        return;
      }
      this.$refs.excelDownloader.download();
    },
    downloadChartAsImage() {
      const chartInstance = this.$refs.usageChart?.chart;

      exportChartAsPNG(chartInstance, `USAGE_Chart_${format(new Date(), 'yyyy-MM-dd')}`, {
        width: 1200,
        height: 600,
        scale: 2,
        surface: 'usage',
      });
    },
    async onFilterChange({ startDate, endDate, fromHour, toHour, selectedDate }) {
      this.selectedDate = selectedDate;
      this.filters = { startDate, endDate, fromHour, toHour };

      await this.fetchUsageData({
        startDate,
        endDate,
        fromHour,
        toHour,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-left-title {
  color: #363647;
}
.chart-right-title {
  color: #16883a;
}
</style>
